<p-toast key="toastmsg" position="top-right"></p-toast>

<section class="delete-account-page">
  <div class="container delete-account-container">
    <h1>Delete your TPL App account</h1>
    <p class="intro-copy">
      This page allows users of TPL App, operated by Skygoal Sports Private Limited, to request deletion of their
      account and associated personal data.
    </p>

    <div class="content-section">
      <h2>Once a deletion request is submitted:</h2>
      <ol>
        <li>Send an account deletion request from the button below using your registered mobile number.</li>
        <li>Verify the mobile number registered with your TPL App account.</li>
        <li>Select a reason and submit your account deletion request.</li>
        <li>Account deletion requests are generally processed within 48 hours.</li>
      </ol>
    </div>

    <div class="content-section">
      <h2>Data that will be deleted</h2>
      <p>
        Upon approval of an account deletion request, your account will be removed from active use. Certain information associated with your account may be retained where necessary for legal, regulatory, accounting, security, fraud prevention, dispute resolution, or record-keeping purposes.
      </p>
    </div>

    <div class="content-section">
      <h2>The types of information that may be retained include:</h2>
      <ul class="retention-list">
        <li><span>*</span>Transaction history, including wallet-related transactions, for accounting and financial record requirements.</li>
        <li><span>*</span>Tournament participation records and related history.</li>
        <li><span>*</span>Other records required to maintain platform integrity, comply with applicable laws, and resolve disputes.</li>
      </ul>
      <p>
        Retained information will only be stored for the period necessary to fulfill the applicable legal or business requirements and will be securely handled in accordance with this 
        <a routerLink="/privacy-policy" class="policy-link">Privacy Policy</a>.
      </p>
    </div>

    <div class="content-section">
      <h2>Processing time</h2>
      <p>
        Account deletion requests are normally reviewed and processed within a reasonable period after identity
        verification is completed. You will receive a confirmation after your request is submitted and processed within 48 hours.
      </p>
    </div>

    <div class="request-box" *ngIf="currentStep === 'intro'">
      <button type="button" pButton pRipple label="Start account deletion" class="delete-danger-btn"
        (click)="startDeleteRequest()"></button>
      <p>
        Need help? Contact
        <a href="mailto:support@tplsport.com?subject=TPL%20App%20account%20deletion" class="policy-link">support@tplsport.com</a>.
      </p>
    </div>

    <div class="request-box verification-box" *ngIf="currentStep === 'mobile'">
      <h2>Verify your registered mobile number</h2>
      <p>We will send a 4 digit OTP before showing the account deletion request form.</p>

      <form [formGroup]="mobileForm" (ngSubmit)="sendOtp()" class="mobile-form">
        <div class="mobile-input-row">
          <div class="phone-prefix">
            <p-dropdown inputId="phoneCode" formControlName="phoneCode" appendTo="body" [options]="countryCodeList"
              optionLabel="phone_code" (onChange)="setCountryCode($event)" [filter]="true"
              [ngClass]="{ 'ng-dirty ng-invalid': submittedMobile && mobileFormControls.phoneCode.errors }">
              <ng-template pTemplate="selectedItem" let-selectedOption>
                <div class="country-option" *ngIf="selectedOption">
                  <img src="https://primefaces.org/cdn/primeng/images/demo/flag/flag_placeholder.png"
                    [class]="'flag flag-' + selectedOption.code.toLowerCase()" />
                  <strong>{{ selectedOption.phone_code }}</strong>
                </div>
              </ng-template>
              <ng-template let-country pTemplate="item">
                <div class="country-option">
                  <img src="https://primefaces.org/cdn/primeng/images/demo/flag/flag_placeholder.png"
                    [class]="'flag flag-' + country.code.toLowerCase()" />
                  <span>{{ country?.phone_code }}</span>
                </div>
              </ng-template>
            </p-dropdown>
          </div>
          <input id="mobileNumber" type="tel" inputmode="numeric" maxlength="10" pInputText
            formControlName="mobileNumber" placeholder="Mobile Number"
            [ngClass]="{ 'is-invalid': submittedMobile && mobileFormControls.mobileNumber.errors }">
        </div>
        <div class="invalid-feedback"
          *ngIf="submittedMobile && mobileFormControls.mobileNumber.hasError('required')">
          Mobile number is required.
        </div>
        <div class="invalid-feedback"
          *ngIf="submittedMobile && mobileFormControls.mobileNumber.hasError('pattern')">
          Enter a valid 10 digit mobile number.
        </div>

        <button type="submit" pButton pRipple [label]="sendingOtp ? 'Sending OTP...' : 'Send OTP'"
          class="delete-danger-btn" [disabled]="sendingOtp"></button>
        <p class="terms-copy">
          By clicking 'Send OTP', you acknowledge that you have read and understood our
          <a routerLink="/termsandcondition">Terms and Conditions</a>
        </p>
      </form>
    </div>

    <div class="request-box delete-form-box" *ngIf="currentStep === 'delete'">
      <div class="verified-strip">
        <i class="pi pi-check-circle"></i>
        <span>Mobile verified: +{{ selectedCountryCode?.phone_code || '91' }} {{ verifiedMobileNumber }}</span>
      </div>

      <h2>Request To Delete Account</h2>
      <form [formGroup]="deleteAccountForm" class="delete-request-form" (ngSubmit)="submitDeleteAccount()">
        <div class="p-field">
          <label for="reason">Reason</label>
          <p-dropdown inputId="reason" [options]="reasonList" formControlName="reason" [filter]="true"
            placeholder="Select Reason" optionLabel="label" appendTo="body"
            [ngClass]="{ 'is-invalid': submittedDeleteAccount && deleteReqForm.reason.errors }"
            (onChange)="onReasonSelect($event)"></p-dropdown>
          <div *ngIf="submittedDeleteAccount && deleteReqForm.reason.hasError('required')" class="invalid-feedback">
            Reason is required.
          </div>
        </div>

        <div class="p-field" *ngIf="showMessageInput">
          <label for="message">Message</label>
          <textarea inputId="message" rows="4" class="form-control" formControlName="message" pInputTextarea></textarea>
        </div>

        <button type="submit" pButton pRipple
          [label]="checkingDeleteRequestStatus ? 'Checking Status...' : (submittingDeleteRequest ? 'Sending Request...' : 'Send Request')"
          class="delete-danger-btn" [disabled]="checkingDeleteRequestStatus || submittingDeleteRequest"></button>
      </form>
    </div>

    <div class="request-box success-box" *ngIf="currentStep === 'success'">
      <h2>Delete account request submitted</h2>
      <p>
        Your TPL App account deletion request has been submitted successfully or already exists.
        Account deletion will be processed within 48 hours.
      </p>
    </div>

    <div class="request-box unavailable-box" *ngIf="currentStep === 'unavailable'">
      <h2>Account not eligible for deletion</h2>
      <p>{{ statusModalDescription }}</p>
      <p>
        Need help? Contact
        <a href="mailto:support@tplsport.com?subject=TPL%20App%20account%20deletion" class="policy-link">support@tplsport.com</a>.
      </p>
    </div>
  </div>
</section>

<p-dialog [(visible)]="showOtpmodal" [modal]="true" [style]="{ width: '500px' }"
  [draggable]="false" [resizable]="false" id="OTP">
  <div class="otp-dialog">
    <h3>Enter OTP</h3>
    <p>A 4 digit OTP has been sent to +{{ selectedCountryCode?.phone_code || '91' }} {{ mobileNumber }}</p>

    <div id="otp" class="inputs d-flex justify-content-center flex-row mt-2 mb-2">
      <ng-otp-input #ngOtpInput (onInputChange)="onOtpChange($event)" [config]="config"></ng-otp-input>
    </div>

    <div *ngIf="showOtpErrors && !showOtpErrorsMsg" class="otp-error">
      Please enter OTP.
    </div>
    <div *ngIf="showOtpErrorsMsg" class="otp-error">
      {{ otpErrorMessage }}
    </div>

    <div class="otp-resend">
      <span *ngIf="!showResendButton">Resend in {{ displayTime }}</span>
      <button type="button" *ngIf="showResendButton" class="link-button" (click)="resendOtp()"
        [disabled]="sendingOtp">
        Resend
      </button>
    </div>

    <button type="button" pButton pRipple class="delete-danger-btn"
      [label]="checkingDeleteRequestStatus ? 'Checking Status...' : (verifyingOtp ? 'Verifying...' : 'Verify & Proceed')"
      [disabled]="verifyingOtp || checkingDeleteRequestStatus"
      (click)="verifyAndProceed()"></button>
  </div>
</p-dialog>

<p-dialog [(visible)]="showAccountSelectionModal" [modal]="true"
  [style]="{ width: '520px' }" [draggable]="false" [resizable]="false">
  <div class="account-selection-dialog">
    <h3>Choose account</h3>
    <p>We found more than one account for +{{ selectedCountryCode?.phone_code || '91' }} {{ verifiedMobileNumber }}.</p>

    <button type="button" class="account-option" *ngFor="let account of accountOptions"
      [ngClass]="{ 'blocked-account': !account.canDelete }" (click)="selectAccountForDeletion(account)">
      <span>
        <strong>{{ account.label }}</strong>
        <small *ngIf="account.fullName">{{ account.fullName }}</small>
        <small>{{ account.roleName }}</small>
      </span>
      <em>{{ account.canDelete ? 'Continue' : 'Continue' }}</em>
    </button>
  </div>
</p-dialog>

<p-dialog [(visible)]="showDeleteRequestStatusModal" [modal]="true"
  [style]="{ width: '520px' }" [draggable]="false" [resizable]="false">
  <div class="status-dialog">
    <i class="pi pi-check-circle"></i>
    <h3>{{ statusModalTitle }}</h3>
    <p>{{ statusModalDescription }}</p>

    <!-- <div class="status-details" *ngIf="existingDeleteRequest">
      <div *ngIf="existingDeleteRequest.created_at || existingDeleteRequest.createdAt">
        <span>Requested On</span>
        <strong>{{ (existingDeleteRequest.created_at || existingDeleteRequest.createdAt) | date : 'dd-MM-yyyy' }}</strong>
      </div>
      <div *ngIf="existingDeleteRequest.reason">
        <span>Reason</span>
        <strong>{{ existingDeleteRequest.reason }}</strong>
      </div>
      <div *ngIf="existingDeleteRequest.mobileNumber">
        <span>Mobile Number</span>
        <strong>{{ existingDeleteRequest.mobileNumber }}</strong>
      </div>
    </div>

    <button type="button" pButton pRipple label="Close" class="delete-danger-btn"
      (click)="showDeleteRequestStatusModal = false"></button> -->
  </div>
</p-dialog>
